"use client";

import * as React from "react";
import { cn } from "@/lib/utils";

const wensityCardSurfaceClass = cn(
  "relative isolate overflow-hidden rounded-2xl",
  "border border-black/[0.08] bg-[radial-gradient(ellipse_90%_70%_at_50%_-10%,#ffffff_0%,#f4f4f5_72%)] [box-shadow:0_1px_2px_rgba(0,0,0,.06),0_8px_24px_-12px_rgba(0,0,0,.08)]",
  "dark:border-white/[0.06] dark:bg-[radial-gradient(ellipse_90%_70%_at_50%_-10%,#15151b_0%,#0a0a0b_72%)] dark:[box-shadow:0_1px_2px_rgba(0,0,0,.4),0_8px_24px_-12px_rgba(0,0,0,.6)]",
);

export type SwitchSize = "sm" | "md" | "lg";
export type SwitchVariant = "minimal" | "elevated" | "segmented" | "power";

export interface SwitchProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    "checked" | "defaultChecked" | "onChange" | "size" | "type"
  > {
  checked?: boolean;
  defaultChecked?: boolean;
  onCheckedChange?: (
    checked: boolean,
    event: React.ChangeEvent<HTMLInputElement>,
  ) => void;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  label?: React.ReactNode;
  description?: React.ReactNode;
  error?: React.ReactNode;
  switchSize?: SwitchSize;
  variant?: SwitchVariant;
  labelPosition?: "left" | "right";
  fullWidth?: boolean;
  containerClassName?: string;
  controlClassName?: string;
  labelClassName?: string;
  descriptionClassName?: string;
}

export interface SwitchGroupProps extends React.HTMLAttributes<HTMLFieldSetElement> {
  label?: React.ReactNode;
  description?: React.ReactNode;
  error?: React.ReactNode;
  children: React.ReactNode;
  divided?: boolean;
}

export interface SwitchCardProps extends SwitchProps {
  leading?: React.ReactNode;
  /** Static status label when trailingOn/trailingOff are not set. */
  trailing?: React.ReactNode;
  /** Status label when the switch is on. */
  trailingOn?: React.ReactNode;
  /** Status label when the switch is off. */
  trailingOff?: React.ReactNode;
}

export interface SwitchSettingsGroupProps {
  title: string;
  description?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

const trackSizes: Record<SwitchSize, string> = {
  sm: "h-5 w-9",
  md: "h-6 w-11",
  lg: "h-7 w-[3.25rem]",
};

const knobSizes: Record<SwitchSize, string> = {
  sm: "size-4",
  md: "size-5",
  lg: "size-6",
};

const segmentedSizes: Record<SwitchSize, string> = {
  sm: "h-7 w-[4.5rem] text-[10px]",
  md: "h-8 w-[5.25rem] text-[11px]",
  lg: "h-9 w-24 text-xs",
};

const iconSizes: Record<SwitchSize, string> = {
  sm: "size-7",
  md: "size-8",
  lg: "size-9",
};

const labelSizes: Record<SwitchSize, string> = {
  sm: "text-[13px]",
  md: "text-sm",
  lg: "text-[15px]",
};

const rowGap: Record<SwitchSize, string> = {
  sm: "gap-3",
  md: "gap-3.5",
  lg: "gap-4",
};

const controlOffset: Record<SwitchSize, string> = {
  sm: "mt-px",
  md: "mt-0.5",
  lg: "mt-0.5",
};

const focusRing = cn(
  "peer-focus-visible:outline-none",
  "peer-focus-visible:ring-2 peer-focus-visible:ring-[color-mix(in_srgb,var(--foreground)_22%,transparent)]",
  "peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-[var(--background)]",
);

const SWITCH_STYLE_ID = "wensity-switch-styles-v3";
const EASE_OUT_CSS = "cubic-bezier(0.16, 1, 0.3, 1)";
const EASE_DRAWER_CSS = "cubic-bezier(0.32, 0.72, 0, 1)";

function ensureSwitchStyles() {
  if (typeof document === "undefined") return;
  if (document.getElementById(SWITCH_STYLE_ID)) return;

  document.getElementById("wensity-switch-styles-v1")?.remove();
  document.getElementById("wensity-switch-styles-v2")?.remove();

  const style = document.createElement("style");
  style.id = SWITCH_STYLE_ID;
  style.textContent = `
    [data-slot="switch-track"] {
      transition:
        background-color 280ms ${EASE_OUT_CSS},
        border-color 280ms ${EASE_OUT_CSS},
        box-shadow 280ms ${EASE_OUT_CSS},
        color 220ms ${EASE_OUT_CSS},
        opacity 220ms ${EASE_OUT_CSS};
    }

    [data-slot="switch-knob"] {
      transform: translate3d(0, 0, 0);
      transition:
        transform 320ms ${EASE_DRAWER_CSS},
        box-shadow 280ms ${EASE_OUT_CSS},
        opacity 220ms ${EASE_OUT_CSS};
    }

    [data-slot="switch-control"][data-state="checked"] [data-slot="switch-knob"][data-size="sm"] {
      transform: translate3d(16px, 0, 0);
      transition-duration: 320ms;
    }

    [data-slot="switch-control"][data-state="checked"] [data-slot="switch-knob"][data-size="md"] {
      transform: translate3d(20px, 0, 0);
      transition-duration: 320ms;
    }

    [data-slot="switch-control"][data-state="checked"] [data-slot="switch-knob"][data-size="lg"] {
      transform: translate3d(24px, 0, 0);
      transition-duration: 320ms;
    }

    [data-slot="switch-control"][data-state="unchecked"] [data-slot="switch-knob"] {
      transition-duration: 240ms;
    }

    [data-slot="switch-control"][data-variant="segmented"] {
      transition:
        background-color 280ms ${EASE_OUT_CSS},
        border-color 280ms ${EASE_OUT_CSS},
        box-shadow 280ms ${EASE_OUT_CSS};
    }

    [data-slot="switch-segment-indicator"] {
      transform: translate3d(0, 0, 0);
      transition: transform 360ms ${EASE_DRAWER_CSS};
    }

    [data-slot="switch-control"][data-state="checked"] [data-slot="switch-segment-indicator"] {
      transform: translate3d(100%, 0, 0);
      transition-duration: 360ms;
    }

    [data-slot="switch-control"][data-state="unchecked"] [data-slot="switch-segment-indicator"] {
      transition-duration: 280ms;
    }

    [data-slot="switch-segment-label"] {
      transition: color 240ms ${EASE_OUT_CSS};
    }

    [data-slot="switch-power-icon"] {
      position: absolute;
      inset: 0;
      display: grid;
      place-items: center;
      opacity: 0;
      transform: scale(0.92);
      transition:
        opacity 200ms ${EASE_OUT_CSS},
        transform 240ms ${EASE_DRAWER_CSS};
    }

    [data-slot="switch-power-icon"][data-active="true"] {
      opacity: 1;
      transform: scale(1);
      transition-duration: 260ms;
    }

    [data-slot="switch-power-check"] path {
      stroke-dasharray: 1;
      stroke-dashoffset: 1;
      transition: stroke-dashoffset 280ms ${EASE_OUT_CSS} 70ms;
    }

    [data-slot="switch-control"][data-state="checked"] [data-slot="switch-power-check"] path {
      stroke-dashoffset: 0;
      transition-duration: 300ms;
      transition-delay: 90ms;
    }

    [data-slot="switch-control"][data-state="unchecked"] [data-slot="switch-power-check"] path {
      stroke-dashoffset: 1;
      transition-duration: 160ms;
      transition-delay: 0ms;
    }

    [data-slot="switch-card-trailing"] {
      transition:
        border-color 220ms ${EASE_OUT_CSS},
        color 220ms ${EASE_OUT_CSS},
        opacity 200ms ${EASE_OUT_CSS};
    }

    [data-slot="switch-card-trailing"][data-swapping="true"] {
      opacity: 0.72;
    }

    @media (prefers-reduced-motion: reduce) {
      [data-slot="switch-track"],
      [data-slot="switch-knob"],
      [data-slot="switch-control"][data-variant="segmented"],
      [data-slot="switch-segment-indicator"],
      [data-slot="switch-segment-label"],
      [data-slot="switch-power-icon"],
      [data-slot="switch-card-trailing"] {
        transition-duration: 0ms !important;
        transition-delay: 0ms !important;
      }

      [data-slot="switch-control"][data-state="checked"] [data-slot="switch-knob"][data-size="sm"] {
        transform: translate3d(16px, 0, 0);
      }

      [data-slot="switch-control"][data-state="checked"] [data-slot="switch-knob"][data-size="md"] {
        transform: translate3d(20px, 0, 0);
      }

      [data-slot="switch-control"][data-state="checked"] [data-slot="switch-knob"][data-size="lg"] {
        transform: translate3d(24px, 0, 0);
      }

      [data-slot="switch-control"][data-state="checked"] [data-slot="switch-segment-indicator"] {
        transform: translate3d(100%, 0, 0);
      }

      [data-slot="switch-power-icon"][data-active="true"] {
        opacity: 1;
        transform: none;
      }

      [data-slot="switch-power-icon"][data-active="false"] {
        opacity: 0;
      }

      [data-slot="switch-control"][data-state="checked"] [data-slot="switch-power-check"] path {
        stroke-dashoffset: 0;
      }

      [data-slot="switch-card-trailing"][data-swapping="true"] {
        opacity: 1;
      }
    }
  `;
  document.head.appendChild(style);
}

const smoothTrack = "motion-reduce:transition-none";

/** Recessed rail — visible against page bg in both themes. */
const trackOff = cn(
  "border border-transparent",
  "bg-[color-mix(in_srgb,var(--foreground)_8%,var(--background))]",
  "dark:bg-[color-mix(in_srgb,var(--foreground)_14%,var(--background))]",
);

const trackOffHover = cn(
  "data-[state=unchecked]:hover:[&_[data-slot=switch-track]]:bg-[color-mix(in_srgb,var(--foreground)_12%,var(--background))]",
  "dark:data-[state=unchecked]:hover:[&_[data-slot=switch-track]]:bg-[color-mix(in_srgb,var(--foreground)_18%,var(--background))]",
);

/** Active pill rail — soft graphite in light; mid-tone in dark so white thumb stays visible. */
const trackOnPill = cn(
  "border border-[color-mix(in_srgb,var(--foreground)_10%,transparent)]",
  "bg-[color-mix(in_srgb,var(--foreground)_58%,var(--background))]",
  "shadow-[0_1px_2px_rgba(0,0,0,0.10),0_0_0_1px_rgba(0,0,0,0.04)]",
  "dark:border-[color-mix(in_srgb,var(--foreground)_24%,transparent)]",
  "dark:bg-[color-mix(in_srgb,var(--foreground)_62%,var(--background))]",
  "dark:shadow-[0_1px_3px_rgba(0,0,0,0.45),0_0_0_1px_rgba(255,255,255,0.06)]",
);

/** Active power rail — full foreground fill; icon provides contrast (no white thumb). */
const trackOnPower = cn(
  "border border-[color-mix(in_srgb,var(--foreground)_10%,transparent)]",
  "bg-[var(--foreground)]",
  "shadow-[0_1px_2px_rgba(0,0,0,0.10),0_0_0_1px_rgba(0,0,0,0.04)]",
  "dark:shadow-[0_1px_3px_rgba(0,0,0,0.45),0_0_0_1px_rgba(255,255,255,0.06)]",
);

const trackOnError = cn(
  "border-red-500/40 bg-red-600",
  "shadow-[0_1px_2px_rgba(127,29,29,0.22)]",
  "dark:border-red-400/35 dark:bg-red-500 dark:shadow-[0_1px_2px_rgba(0,0,0,0.35)]",
);

/** Thumb stays white in every state — only the rail changes (iOS-style). */
const knobBase = cn(
  "pointer-events-none rounded-full bg-white transform-gpu",
  "shadow-[0_1px_2px_rgba(0,0,0,0.18),0_0_0_1px_rgba(0,0,0,0.05)]",
  "dark:shadow-[0_1px_3px_rgba(0,0,0,0.50),0_0_0_1px_rgba(255,255,255,0.08)]",
);

const knobElevated = cn(
  "shadow-[0_2px_4px_rgba(0,0,0,0.14),0_0_0_1px_rgba(0,0,0,0.05)]",
  "dark:shadow-[0_2px_5px_rgba(0,0,0,0.52),0_0_0_1px_rgba(255,255,255,0.10)]",
);

/** Segmented / power shell (off). */
const shellOff = cn(
  "border border-[var(--border-strong)]",
  "bg-[color-mix(in_srgb,var(--foreground)_6%,var(--background))]",
  "shadow-[0_1px_2px_rgba(0,0,0,0.04)]",
  "dark:bg-[color-mix(in_srgb,var(--foreground)_10%,var(--background))]",
  "dark:shadow-[0_1px_2px_rgba(0,0,0,0.28)]",
);

/** Segmented shell when on (non-error). */
const shellOn = cn(
  "border border-[color-mix(in_srgb,var(--foreground)_12%,transparent)]",
  "bg-[color-mix(in_srgb,var(--foreground)_9%,var(--background))]",
  "shadow-[0_1px_2px_rgba(0,0,0,0.04)]",
  "dark:border-white/14 dark:bg-[color-mix(in_srgb,var(--foreground)_16%,var(--background))]",
  "dark:shadow-[0_1px_2px_rgba(0,0,0,0.28)]",
);

function knobClasses(elevatedKnob: boolean) {
  return cn(knobBase, elevatedKnob && knobElevated);
}

function resolvePillTrack(
  checked: boolean,
  error?: React.ReactNode,
  mode: "pill" | "power" = "pill",
) {
  if (error && checked) return trackOnError;
  if (checked) return mode === "power" ? trackOnPower : trackOnPill;
  return trackOff;
}

function resolveSegmentedShell(checked: boolean, error?: React.ReactNode) {
  if (error && checked) {
    return "border border-red-500/40 bg-red-600/15 dark:border-red-400/35 dark:bg-red-500/20";
  }
  if (checked) return shellOn;
  return shellOff;
}

function SwitchCheckIcon({
  className,
  slot,
}: {
  className?: string;
  slot?: string;
}) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 16 16"
      fill="none"
      data-slot={slot}
      className={cn("size-3.5", className)}
    >
      <path
        d="M3.5 8.2 6.4 11.1 12.6 4.9"
        stroke="currentColor"
        strokeWidth="1.85"
        strokeLinecap="round"
        strokeLinejoin="round"
        pathLength={1}
      />
    </svg>
  );
}

function SwitchPowerIcon({
  checked,
  error,
}: {
  checked: boolean;
  error?: React.ReactNode;
}) {
  return (
    <span className="relative size-3.5">
      <span
        data-slot="switch-power-icon"
        data-active={!checked}
        aria-hidden="true"
      >
        <svg viewBox="0 0 16 16" fill="none" className="size-3.5 text-[var(--muted-foreground)]">
          <path
            d="M8 3.25v3.5"
            stroke="currentColor"
            strokeWidth="1.65"
            strokeLinecap="round"
          />
          <path
            d="M5.35 5.35a3.65 3.65 0 1 0 5.3 0"
            stroke="currentColor"
            strokeWidth="1.65"
            strokeLinecap="round"
          />
        </svg>
      </span>
      <span
        data-slot="switch-power-icon"
        data-active={checked}
        aria-hidden="true"
      >
        <SwitchCheckIcon
          slot="switch-power-check"
          className={error ? "text-white" : "text-[var(--background)]"}
        />
      </span>
    </span>
  );
}

const segmentActiveText = "text-neutral-950";
const segmentInactiveText = "text-[var(--muted-foreground)]";

function SwitchControl({
  checked,
  disabled,
  error,
  switchSize,
  variant,
  className,
}: {
  checked: boolean;
  disabled?: boolean;
  error?: React.ReactNode;
  switchSize: SwitchSize;
  variant: SwitchVariant;
  className?: string;
}) {
  if (variant === "segmented") {
    return (
      <span
        data-slot="switch-control"
        data-variant="segmented"
        data-state={checked ? "checked" : "unchecked"}
        className={cn(
          "relative grid shrink-0 grid-cols-2 items-center overflow-hidden rounded-full p-0.5",
          smoothTrack,
          resolveSegmentedShell(checked, error),
          focusRing,
          disabled && "opacity-40",
          segmentedSizes[switchSize],
          className,
        )}
      >
        <span
          data-slot="switch-segment-indicator"
          aria-hidden="true"
          className={cn(
            "absolute inset-y-0.5 left-0.5 w-[calc(50%-2px)] rounded-full",
            checked && error
              ? cn(
                  "bg-red-600 shadow-[0_1px_2px_rgba(127,29,29,0.28)]",
                  "dark:bg-red-500 dark:shadow-[0_1px_2px_rgba(0,0,0,0.40)]",
                )
              : knobBase,
          )}
        />
        <span
          data-slot="switch-segment-label"
          className={cn(
            "relative z-[2] text-center font-medium tracking-[-0.01em]",
            checked ? segmentInactiveText : segmentActiveText,
          )}
        >
          Off
        </span>
        <span
          data-slot="switch-segment-label"
          className={cn(
            "relative z-[2] text-center font-medium tracking-[-0.01em]",
            checked
              ? error
                ? "text-white"
                : segmentActiveText
              : segmentInactiveText,
          )}
        >
          On
        </span>
      </span>
    );
  }

  if (variant === "power") {
    return (
      <span
        data-slot="switch-control"
        data-variant="power"
        data-state={checked ? "checked" : "unchecked"}
        className={cn(
          "relative inline-flex shrink-0",
          disabled && "opacity-40",
          className,
        )}
      >
        <span
          data-slot="switch-track"
          className={cn(
            "relative inline-flex shrink-0 items-center justify-center rounded-full",
            smoothTrack,
            resolvePillTrack(checked, error, "power"),
            focusRing,
            iconSizes[switchSize],
          )}
        >
          <SwitchPowerIcon checked={checked} error={error} />
        </span>
      </span>
    );
  }

  const elevatedKnob = variant === "elevated";

  return (
    <span
      data-slot="switch-control"
      data-variant={variant}
      data-state={checked ? "checked" : "unchecked"}
      className={cn(
        "relative inline-flex shrink-0",
        disabled && "opacity-40",
        className,
      )}
    >
      <span
        data-slot="switch-track"
        className={cn(
          "relative inline-flex shrink-0 items-center rounded-full p-0.5",
          smoothTrack,
          resolvePillTrack(checked, error),
          focusRing,
          elevatedKnob &&
            !checked &&
            "shadow-[0_1px_2px_rgba(0,0,0,0.06)] dark:shadow-[0_1px_2px_rgba(0,0,0,0.32)]",
          trackSizes[switchSize],
        )}
      >
        <span
          data-slot="switch-knob"
          data-size={switchSize}
          aria-hidden="true"
          className={cn(knobClasses(elevatedKnob), knobSizes[switchSize])}
        />
      </span>
    </span>
  );
}

export const Switch = React.forwardRef<HTMLInputElement, SwitchProps>(
  (
    {
      id,
      className,
      containerClassName,
      controlClassName,
      labelClassName,
      descriptionClassName,
      label,
      description,
      error,
      switchSize = "md",
      variant = "minimal",
      labelPosition = "right",
      fullWidth = true,
      checked,
      defaultChecked = false,
      disabled,
      onCheckedChange,
      onChange,
      "aria-describedby": ariaDescribedBy,
      ...props
    },
    ref,
  ) => {
    const reactId = React.useId();
    const switchId = id ?? `ui-switch-${reactId}`;
    const descriptionId = description ? `${switchId}-description` : undefined;
    const errorId = error ? `${switchId}-error` : undefined;
    const isControlled = checked !== undefined;
    const [uncontrolledChecked, setUncontrolledChecked] =
      React.useState(defaultChecked);
    const isChecked = isControlled ? checked : uncontrolledChecked;
    const describedBy =
      [ariaDescribedBy, errorId, descriptionId].filter(Boolean).join(" ") ||
      undefined;
    const hasCopy = Boolean(label || description || error);
    const alignStart = Boolean(description || error);

    React.useEffect(() => {
      ensureSwitchStyles();
    }, []);

    const handleChange = React.useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const nextChecked = event.currentTarget.checked;
        if (!isControlled) setUncontrolledChecked(nextChecked);
        onCheckedChange?.(nextChecked, event);
        onChange?.(event);
      },
      [isControlled, onChange, onCheckedChange],
    );

    const control = (
      <>
        <input
          ref={ref}
          id={switchId}
          type="checkbox"
          role="switch"
          checked={isChecked}
          disabled={disabled}
          aria-checked={isChecked}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          onChange={handleChange}
          className="peer sr-only"
          {...props}
        />
        <SwitchControl
          checked={isChecked}
          disabled={disabled}
          error={error}
          switchSize={switchSize}
          variant={variant}
          className={controlClassName}
        />
      </>
    );

    const copy = hasCopy ? (
      <span className="min-w-0 flex-1">
        {label ? (
          typeof label === "string" || typeof label === "number" ? (
            <span
              className={cn(
                "block font-medium leading-snug tracking-[-0.015em] text-[var(--foreground)]",
                labelSizes[switchSize],
                disabled && "text-[var(--muted-foreground)]",
                labelClassName,
              )}
            >
              {label}
            </span>
          ) : (
            label
          )
        ) : null}
        {description ? (
          <span
            id={descriptionId}
            className={cn(
              "mt-1 block text-[12px] leading-5 text-[var(--muted-foreground)]",
              descriptionClassName,
            )}
          >
            {description}
          </span>
        ) : null}
        {error ? (
          <span
            id={errorId}
            className="mt-1 block text-[12px] leading-5 text-red-600 dark:text-red-400"
          >
            {error}
          </span>
        ) : null}
      </span>
    ) : null;

    return (
      <label
        data-slot="switch"
        data-state={isChecked ? "checked" : "unchecked"}
        data-disabled={disabled ? "" : undefined}
        data-error={error ? "" : undefined}
        className={cn(
          "group/switch relative flex cursor-pointer select-none",
          alignStart ? "items-start" : "items-center",
          rowGap[switchSize],
          fullWidth ? "w-full" : "w-fit",
          disabled && "cursor-not-allowed",
          labelPosition === "left" && "justify-between",
          !disabled && trackOffHover,
          containerClassName,
          className,
        )}
      >
        {labelPosition === "left" ? (
          <>
            {copy}
            <span className={cn("shrink-0", alignStart && controlOffset[switchSize])}>
              {control}
            </span>
          </>
        ) : (
          <>
            <span className={cn("shrink-0", alignStart && controlOffset[switchSize])}>
              {control}
            </span>
            {copy}
          </>
        )}
      </label>
    );
  },
);

Switch.displayName = "Switch";

export function SwitchGroup({
  label,
  description,
  error,
  children,
  divided = true,
  className,
  ...props
}: SwitchGroupProps) {
  const reactId = React.useId();
  const descriptionId = description
    ? `ui-switch-group-${reactId}-description`
    : undefined;
  const errorId = error ? `ui-switch-group-${reactId}-error` : undefined;

  return (
    <fieldset
      aria-describedby={[descriptionId, errorId].filter(Boolean).join(" ") || undefined}
      className={cn("space-y-3", className)}
      {...props}
    >
      {label || description ? (
        <div className="space-y-1">
          {label ? (
            <legend className="text-sm font-semibold tracking-[-0.015em] text-[var(--foreground)]">
              {label}
            </legend>
          ) : null}
          {description ? (
            <p
              id={descriptionId}
              className="text-[12px] leading-5 text-[var(--muted-foreground)]"
            >
              {description}
            </p>
          ) : null}
        </div>
      ) : null}
      <div
        className={cn(
          divided && wensityCardSurfaceClass,
          divided &&
            "[&>[data-slot=switch]]:border-b [&>[data-slot=switch]]:border-[var(--border)] [&>[data-slot=switch]]:px-4 [&>[data-slot=switch]]:py-3.5 [&>[data-slot=switch]:last-child]:border-b-0",
          !divided && "space-y-1",
        )}
      >
        {children}
      </div>
      {error ? (
        <p id={errorId} className="text-[12px] leading-5 text-red-600 dark:text-red-400">
          {error}
        </p>
      ) : null}
    </fieldset>
  );
}

export function SwitchSettingsGroup({
  title,
  description,
  children,
  className,
}: SwitchSettingsGroupProps) {
  return (
    <section className={cn("flex min-h-0 min-w-0 flex-col gap-2.5", className)}>
      <header className="min-h-11 px-0.5">
        <h3 className="text-[13px] font-semibold leading-5 tracking-[-0.015em] text-[var(--foreground)]">
          {title}
        </h3>
        {description ? (
          <p className="mt-0.5 line-clamp-2 text-[12px] leading-5 text-[var(--muted-foreground)]">
            {description}
          </p>
        ) : (
          <p className="mt-0.5 text-[12px] leading-5 text-transparent" aria-hidden="true">
            &nbsp;
          </p>
        )}
      </header>
      <div
        className={cn(
          wensityCardSurfaceClass,
          "[&_[data-slot=switch]]:border-b [&_[data-slot=switch]]:border-[var(--border)]",
          "[&_[data-slot=switch]:last-child]:border-b-0",
        )}
      >
        {children}
      </div>
    </section>
  );
}

export function SwitchCard({
  leading,
  trailing,
  trailingOn,
  trailingOff,
  label,
  description,
  error,
  className,
  switchSize = "md",
  variant = "minimal",
  disabled,
  checked,
  defaultChecked = false,
  onCheckedChange,
  onChange,
  id,
  labelPosition: _labelPosition,
  fullWidth: _fullWidth,
  containerClassName: _containerClassName,
  controlClassName: _controlClassName,
  labelClassName: _labelClassName,
  descriptionClassName: _descriptionClassName,
  ...props
}: SwitchCardProps) {
  const reactId = React.useId();
  const switchId = id ?? `ui-switch-card-${reactId}`;
  const descriptionId = description ? `${switchId}-description` : undefined;
  const errorId = error ? `${switchId}-error` : undefined;
  const isControlled = checked !== undefined;
  const [uncontrolledChecked, setUncontrolledChecked] =
    React.useState(defaultChecked);
  const isChecked = isControlled ? checked : uncontrolledChecked;
  const trailingLabel =
    trailingOn !== undefined || trailingOff !== undefined
      ? isChecked
        ? trailingOn
        : trailingOff
      : trailing;
  const describedBy =
    [errorId, descriptionId].filter(Boolean).join(" ") || undefined;
  const hasAnimatedTrailing =
    trailingOn !== undefined || trailingOff !== undefined;
  const [trailingSwapping, setTrailingSwapping] = React.useState(false);
  const prevCheckedRef = React.useRef(isChecked);

  React.useEffect(() => {
    ensureSwitchStyles();
  }, []);

  React.useEffect(() => {
    if (!hasAnimatedTrailing) {
      prevCheckedRef.current = isChecked;
      return;
    }
    if (prevCheckedRef.current === isChecked) return;
    prevCheckedRef.current = isChecked;
    setTrailingSwapping(true);
    const timeoutId = window.setTimeout(() => setTrailingSwapping(false), 220);
    return () => window.clearTimeout(timeoutId);
  }, [hasAnimatedTrailing, isChecked]);

  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextChecked = event.currentTarget.checked;
      if (!isControlled) setUncontrolledChecked(nextChecked);
      onCheckedChange?.(nextChecked, event);
      onChange?.(event);
    },
    [isControlled, onChange, onCheckedChange],
  );

  return (
    <label
      data-slot="switch"
      data-state={isChecked ? "checked" : "unchecked"}
      data-disabled={disabled ? "" : undefined}
      data-error={error ? "" : undefined}
      htmlFor={switchId}
      className={cn(
        "group/switch flex min-h-16 w-full cursor-pointer select-none items-center gap-3.5 px-4 py-3",
        "transition-[background-color] duration-200 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none",
        !disabled &&
          "hover:bg-[color-mix(in_srgb,var(--foreground)_2%,var(--background))]",
        "data-[state=checked]:bg-[color-mix(in_srgb,var(--foreground)_2.5%,var(--background))]",
        disabled && "cursor-not-allowed opacity-50 hover:bg-transparent",
        className,
      )}
    >
      <input
        id={switchId}
        type="checkbox"
        role="switch"
        checked={isChecked}
        disabled={disabled}
        aria-checked={isChecked}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        onChange={handleChange}
        className="peer sr-only"
        {...props}
      />

      {leading ? (
        <span
          className={cn(
            "switch-card-icon grid size-9 shrink-0 place-items-center rounded-lg",
            "border border-[var(--border)]",
            "bg-[color-mix(in_srgb,var(--foreground)_4%,var(--background))]",
            "text-[var(--muted-foreground)]",
            "transition-[border-color,background-color,color] duration-[220ms] ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none",
            "[&_svg]:size-[17px]",
            "group-data-[state=checked]/switch:border-[color-mix(in_srgb,var(--foreground)_12%,transparent)]",
            "group-data-[state=checked]/switch:bg-[color-mix(in_srgb,var(--foreground)_7%,var(--background))]",
            "group-data-[state=checked]/switch:text-[var(--foreground)]",
            disabled && "opacity-80",
          )}
        >
          {leading}
        </span>
      ) : null}

      <span className="min-w-0 flex-1">
        {label ? (
          <span
            className={cn(
              "block font-medium leading-snug tracking-[-0.015em] text-[var(--foreground)]",
              labelSizes[switchSize],
              disabled && "text-[var(--muted-foreground)]",
            )}
          >
            {label}
          </span>
        ) : null}
        {description ? (
          <span
            id={descriptionId}
            className="mt-0.5 line-clamp-2 block text-[12px] leading-[1.45] text-[var(--muted-foreground)]"
          >
            {description}
          </span>
        ) : null}
        {error ? (
          <span
            id={errorId}
            className="mt-1 block text-[12px] leading-5 text-red-600 dark:text-red-400"
          >
            {error}
          </span>
        ) : null}
      </span>

      <span className="flex shrink-0 items-center gap-2.5 pl-1">
        {trailingLabel ? (
          <span
            aria-live="polite"
            data-slot="switch-card-trailing"
            data-swapping={trailingSwapping ? "true" : undefined}
            className={cn(
              "inline-flex min-w-[3.25rem] items-center justify-center rounded-md border border-[var(--border)]",
              "bg-[color-mix(in_srgb,var(--foreground)_3%,var(--background))] px-1.5 py-0.5",
              "text-[10px] font-medium tracking-[0.01em] text-[var(--muted-foreground)]",
              "group-data-[state=checked]/switch:border-[color-mix(in_srgb,var(--foreground)_10%,transparent)]",
              "group-data-[state=checked]/switch:text-[var(--foreground)]",
            )}
          >
            {trailingLabel}
          </span>
        ) : null}
        <SwitchControl
          checked={isChecked}
          disabled={disabled}
          error={error}
          switchSize={switchSize}
          variant={variant}
        />
      </span>
    </label>
  );
}
